import {css} from '@emotion/css';
import {CustomerItemStatus, ResaleType, marketToTimezone, renderDate} from '@remoov/functional';
import {Button, theme} from '@remoov/remoov-components';
import React from 'react';
import {
  BoldInfoText,
  CustomerItemStatusWithBadge,
  HideOnLargeScreen,
  HideOnSmallScreen,
  ImageBox,
  InfoText,
  ItemDetails,
  ItemDetailsContainer,
  ItemDetailsOpenContainer,
  ItemsBoardColumn,
  ItemsBoardContainer,
  ItemsBoardDivisionLine,
  ItemsBoardLeftContainer,
  ItemsBoardRightContainer,
  ItemsBoardRow,
} from './Common';

export type ConsignmentBoardItemProps = {
  id: string | number;
  description: string;
  quantity: number;
  status: string;
  resaleTypeBadge: Maybe<ResaleType>;
  market: string;
  price: string;
  expires: string;
  imgUrl: string;
  alt: string;
  publicNotes: string;
  originalPublicNotes: string;
  setPublicNotes: (value: string) => unknown;
  savePublicNotes: () => unknown;
  itemDetailsClick: () => unknown;
};

export const ConsignmentBoardItem = ({
  id,
  description,
  quantity,
  status,
  resaleTypeBadge,
  market,
  price,
  expires,
  imgUrl,
  alt,
  publicNotes,
  originalPublicNotes,
  setPublicNotes,
  savePublicNotes,
  itemDetailsClick,
}: ConsignmentBoardItemProps) => {
  const timezone = marketToTimezone(market);
  const statusToShowPrice = [
    CustomerItemStatus.AUCTIONED,
    CustomerItemStatus.FOR_SALE,
    CustomerItemStatus.SOLD,
    CustomerItemStatus.PENDING_SALE,
    CustomerItemStatus.PAID,
  ];
  const statusToHideNotes = [
    CustomerItemStatus.SOLD,
    CustomerItemStatus.PENDING_SALE,
    CustomerItemStatus.PAID,
  ];
  return (
    <ItemsBoardContainer className={css({backgroundColor: theme.colors.white})}>
      <ItemsBoardRow className={css({padding: `${theme.spacings.small} 0`})}>
        <HideOnSmallScreen className={css({width: 'auto'})}>
          <ImageBox
            onClick={itemDetailsClick}
            className={css({margin: `0 ${theme.spacings.large}`})}
          >
            <img src={imgUrl} alt={alt} />
          </ImageBox>
        </HideOnSmallScreen>

        <ItemsBoardColumn>
          <ItemsBoardRow
            className={css({
              [theme.mediaMaxWidth.large]: {
                flexWrap: 'wrap',
                padding: `0 ${theme.spacings.xlarge}`,
              },
            })}
          >
            <HideOnLargeScreen
              className={css({
                marginRight: theme.spacings.large,
                [theme.mediaMaxWidth.small_medium]: {
                  width: '100%',
                  margin: 0,
                  display: 'flex',
                  justifyContent: 'center',
                },
              })}
            >
              <ImageBox onClick={itemDetailsClick}>
                <img src={imgUrl} alt={alt} />
              </ImageBox>
            </HideOnLargeScreen>

            <ItemsBoardLeftContainer
              className={css({
                [theme.mediaMaxWidth.large]: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: theme.spacings.xxsmall,
                },
                [theme.mediaMaxWidth.medium]: {width: 'auto'},
              })}
            >
              <ItemsBoardColumn className={css({maxWidth: '100px'})}>
                <BoldInfoText
                  className={css({
                    [theme.mediaMaxWidth.large]: {marginBottom: theme.spacings.xxsmall},
                  })}
                >
                  {id}
                </BoldInfoText>
              </ItemsBoardColumn>
              <ItemsBoardColumn>
                <InfoText>{description}</InfoText>
              </ItemsBoardColumn>
            </ItemsBoardLeftContainer>

            <HideOnSmallScreen>
              <ItemsBoardRightContainer>
                <ItemsBoardColumn>
                  <InfoText>{quantity}</InfoText>
                </ItemsBoardColumn>
                <CustomerItemStatusWithBadge status={status} resaleTypeBadge={resaleTypeBadge} />
                <ItemsBoardColumn>
                  {statusToShowPrice.includes(status) && <BoldInfoText>{price}</BoldInfoText>}
                </ItemsBoardColumn>
                <ItemsBoardColumn>
                  <InfoText>{renderDate(expires, timezone)}</InfoText>
                </ItemsBoardColumn>
              </ItemsBoardRightContainer>
            </HideOnSmallScreen>

            <HideOnLargeScreen className={css({padding: theme.spacings.xxsmall})}>
              <ItemsBoardColumn className={css({div: {marginBottom: theme.spacings.xxsmall}})}>
                <ItemsBoardRow>
                  <BoldInfoText>Quantity: </BoldInfoText>
                  <InfoText>{quantity}</InfoText>
                </ItemsBoardRow>
                <ItemsBoardRow>
                  <BoldInfoText>Status: </BoldInfoText>
                  <CustomerItemStatusWithBadge status={status} resaleTypeBadge={resaleTypeBadge} />
                </ItemsBoardRow>
                {statusToShowPrice.includes(status) && (
                  <ItemsBoardRow>
                    <BoldInfoText>Sales Price: </BoldInfoText>
                    <InfoText className={css({color: '#2E3192', fontWeight: theme.font.bold})}>
                      {price}
                    </InfoText>
                  </ItemsBoardRow>
                )}
                {expires && (
                  <ItemsBoardRow>
                    <BoldInfoText>Expires On: </BoldInfoText>
                    <InfoText>{renderDate(expires, timezone)}</InfoText>
                  </ItemsBoardRow>
                )}
              </ItemsBoardColumn>
            </HideOnLargeScreen>
          </ItemsBoardRow>

          <ItemsBoardRow>
            <ItemDetailsContainer>
              <ItemDetailsOpenContainer>
                {!statusToHideNotes.includes(status) && (
                  <>
                    <ItemDetails
                      placeholder='New notes...'
                      value={publicNotes}
                      onChange={event => setPublicNotes(event.target.value)}
                    />
                    <Button
                      buttonStyle='secondary'
                      minimal
                      onClick={savePublicNotes}
                      className={css({
                        marginLeft: '16px',
                        width: '80px',
                        visibility: publicNotes === originalPublicNotes ? 'hidden' : undefined,
                      })}
                    >
                      Save notes
                    </Button>
                  </>
                )}
              </ItemDetailsOpenContainer>
              <Button buttonStyle='secondary' onClick={itemDetailsClick}>
                Item Details
              </Button>
            </ItemDetailsContainer>
          </ItemsBoardRow>
        </ItemsBoardColumn>
      </ItemsBoardRow>
      <ItemsBoardDivisionLine />
    </ItemsBoardContainer>
  );
};
